import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const startSession = async () => {
    try {
      const res = await fetch(`${API}/coach/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (res.status === 401) {
        setMessages([{ role: "assistant", content: "Please sign in to chat with your MBM Coach. You can log in from the Login page, then reopen the Coach." }]);
        return;
      }
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages([{ role: "assistant", content: data.greeting }]);
    } catch {
      setMessages([{ role: "assistant", content: "I couldn't start right now. Please try again shortly." }]);
    }
  };

  const openChat = () => {
    setOpen(true);
    if (!sessionId) startSession();
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy || !sessionId) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          let obj;
          try { obj = JSON.parse(line); } catch { continue; }
          if (obj.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
              return copy;
            });
          } else if (obj.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: obj.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Something went wrong. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <>
      {!open && (
        <button
          onClick={openChat}
          className="fixed right-4 sm:right-6 z-[2147483000] h-14 w-14 rounded-full flex items-center justify-center bg-[#FAFAFA] text-[#0A0A0A] hover:bg-white shadow-2xl transition-transform hover:scale-105"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)" }}
          aria-label="Open MBM Coach"
          data-testid="coach-open-btn"
        >
          <MessageCircle strokeWidth={1.5} className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#7ee0a1]" style={{ boxShadow: "0 0 8px #7ee0a1" }} />
        </button>
      )}

      {open && (
        <div
          className="fixed right-4 sm:right-6 z-[2147483000] w-[calc(100vw-2rem)] sm:w-[380px] flex flex-col bg-[#0B0B0C]/95 backdrop-blur-xl border border-white/12 rounded-xl overflow-hidden shadow-2xl"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)", height: "min(560px, calc(100vh - 150px))" }}
          data-testid="coach-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <span className="h-8 w-8 rounded-full flex items-center justify-center border border-[#6ea8ff]/40">
                <Sparkles strokeWidth={1.5} className="h-4 w-4 text-[#9fd3ff]" />
              </span>
              <div>
                <div className="font-heading text-sm text-zinc-100 leading-none">MBM Coach</div>
                <div className="section-label text-[7px] text-zinc-500 mt-1">Your personal mentor</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white transition-colors" data-testid="coach-close-btn">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="coach-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm font-light leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#FAFAFA] text-[#0A0A0A] rounded-2xl rounded-br-sm"
                      : "bg-white/[0.06] text-zinc-200 border border-white/10 rounded-2xl rounded-bl-sm"
                  }`}
                  data-testid={`coach-msg-${m.role}`}
                >
                  {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-white/10">
            <div className="flex items-end gap-2">
              <textarea
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Ask your MBM Coach…"
                className="flex-1 resize-none bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-3 py-2.5 text-sm text-white font-light outline-none transition-colors placeholder:text-zinc-600 max-h-28"
                data-testid="coach-input"
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                className="btn-solid h-10 w-10 flex items-center justify-center rounded-lg disabled:opacity-40"
                data-testid="coach-send-btn"
              >
                <Send className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>
            <div className="mt-2 text-[9px] text-zinc-600 font-light text-center">
              Educational only, not financial advice. Teaches the MBM curriculum.
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
